import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getOnboardingQuestions, getUserProgress, updateUserProgress } from '../services/trainingService';

const TrainingContext = createContext();

export const TrainingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentStage, setCurrentStage] = useState(1);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const loadQuestions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getOnboardingQuestions();
      setQuestions(data);
      
      if (currentUser) {
        const userProgress = await getUserProgress(currentUser.uid);
        if (userProgress) {
          setProgress(userProgress);
          setCurrentStage(userProgress.currentStage || 1);
          // Load previous answers if any
          if (userProgress.stages && userProgress.stages[userProgress.currentStage]?.answers) {
            setAnswers(userProgress.stages[userProgress.currentStage].answers);
          }
        }
      }
    } catch (err) {
      console.error('Error loading training data:', err);
      setError('Failed to load training data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const updateAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const submitStage = async () => {
    if (!currentUser) return;
    
    try {
      setIsLoading(true);
      await updateUserProgress(currentUser.uid, currentStage, answers);

      // Keep local progress state in sync with what we just saved
      setProgress((prev) => {
        const prevStages = prev?.stages || {};
        return {
          ...(prev || {}),
          currentStage,
          stages: {
            ...prevStages,
            [currentStage]: {
              completed: true,
              completedAt: new Date().toISOString(),
              answers: { ...answers }
            }
          }
        };
      });
      
      // Move to next stage or complete training
      const nextStage = currentStage + 1;
      const totalStages = Math.max(...questions.map(q => q.stage), 1);
      
      if (nextStage <= totalStages) {
        setCurrentStage(nextStage);
        setAnswers({});
      } else {
        // Training completed
        setProgress(prev => ({
          ...prev,
          completed: true,
          completedAt: new Date().toISOString()
        }));
      }
    } catch (err) {
      console.error('Error saving progress:', err);
      setError('Failed to save your progress. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStageQuestions = (stage) => {
    return questions
      .filter(q => q.stage === stage)
      .sort((a, b) => a.order - b.order);
  };

  const getStageProgress = (stage) => {
    const stageQuestions = getStageQuestions(stage);
    if (!stageQuestions.length) return 0;

    // For the currently active stage, use the in-memory answers so the bar updates live
    if (stage === currentStage) {
      const answered = stageQuestions.filter(
        (q) => answers[q.id] !== undefined && answers[q.id] !== ''
      ).length;
      return Math.round((answered / stageQuestions.length) * 100);
    }

    // For other stages, fall back to persisted progress data if available
    if (!progress?.stages?.[stage]) return 0;
    const answered = Object.keys(progress.stages[stage].answers || {}).length;
    return Math.round((answered / stageQuestions.length) * 100);
  };

  const getOverallProgress = () => {
    if (!questions.length) return 0;

    const totalQuestions = questions.length;
    let answeredCount = 0;

    questions.forEach((q) => {
      const stage = q.stage;

      if (stage === currentStage) {
        // Use in-memory answers for the active stage so it updates live
        const value = answers[q.id];
        if (value !== undefined && value !== '') {
          answeredCount += 1;
        }
      } else if (progress?.stages?.[stage]?.answers) {
        // For other stages, rely on saved progress
        const savedValue = progress.stages[stage].answers[q.id];
        if (savedValue !== undefined && savedValue !== '') {
          answeredCount += 1;
        }
      }
    });

    return Math.round((answeredCount / totalQuestions) * 100);
  };

  const value = {
    questions,
    currentStage,
    setCurrentStage,
    answers,
    updateAnswer,
    submitStage,
    isLoading,
    error,
    progress,
    getStageQuestions,
    getStageProgress,
    getOverallProgress,
    refresh: loadQuestions
  };

  return (
    <TrainingContext.Provider value={value}>
      {children}
    </TrainingContext.Provider>
  );
};

export const useTraining = () => {
  const context = useContext(TrainingContext);
  if (context === undefined) {
    throw new Error('useTraining must be used within a TrainingProvider');
  }
  return context;
};
